import { ApiEpSessionDrag, ApiEpUsers, IApiEpUserAuthorized } from "@smp/api";
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useMount, useSearchParam } from "react-use";

import { SESSION_DRAG_TOKEN_PARAM } from "@/utils";

const KEY = "ep.users.me";

export const useEpUsersMe = (params?: { disabled?: boolean }) => {
  const token = useSearchParam(SESSION_DRAG_TOKEN_PARAM);
  const queryClient = useQueryClient();

  const { isFetched: isSessionDragFinished } = useQuery(
    "ep.session_drag",
    () => ApiEpSessionDrag.read({ path: { token: token || "" } }),
    {
      enabled: !!token,
    },
  );

  const {
    data,
    error: meError,
    isLoading: isMeLoading,
    isFetched: isMeFetched,
  } = useQuery([KEY], ApiEpUsers.Me.retrieve, {
    enabled: params?.disabled !== true && (!token || isSessionDragFinished),
  });

  const { mutate: meUpdatePartial } = useMutation(ApiEpUsers.Me.updatePartial, {
    onSuccess: (me) => {
      queryClient.setQueriesData(KEY, { user: me });
    },
  });

  const { mutate: meEmailChange } = useMutation(ApiEpUsers.common.emailChange, {
    onSuccess: () => {
      meInvalidate();
    },
  });

  const { mutate: mePasswordChange } = useMutation(
    ApiEpUsers.common.passwordChange,
    {
      onSuccess: () => {
        queryClient.setQueryData<typeof data>(KEY, (data) =>
          data?.user
            ? {
                ...data,
                user: {
                  ...data.user,
                  is_set_password: true,
                },
              }
            : data,
        );
      },
    },
  );

  const { mutate: meLoginConfirmEnableConfirm } = useMutation(
    ApiEpUsers.common.loginConfirmEnableConfirm,
  );

  const meInvalidate = useCallback(
    () => queryClient.invalidateQueries(KEY),
    [queryClient],
  );

  const meSetData = useCallback(
    (user: IApiEpUserAuthorized) => {
      queryClient.setQueryData<typeof data>(KEY, (data) => ({
        ...data,
        user,
      }));
    },
    [queryClient],
  );

  useMount(() => {
    const sp = new URLSearchParams(window.location.search);

    if (sp.get(SESSION_DRAG_TOKEN_PARAM)) {
      sp.delete(SESSION_DRAG_TOKEN_PARAM);

      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${sp.size ? "?" + sp.toString() : ""}`,
      );
    }
  });

  return {
    isMeFetched,
    isMeLoading,
    me: data?.user,
    meEmailChange,
    meError,
    meInvalidate,
    meLoginConfirmEnableConfirm,
    mePasswordChange,
    meSetData,
    meUpdatePartial,
  };
};
